/**
 * Helpers for Insured Details ID / BRN validation UX.
 * - Individual: unlock Name + KRA PIN only on iPage errors OR a validation timeout.
 * - Institutional: unlock Name + KRA PIN on any BRN validation failure.
 */

export const VALIDATION_METHOD = {
  KRA: 'KRA',
  IPRS: 'IPRS',
  MANUAL: 'MANUAL',
  IPAGE_MANUAL: 'IPAGE_MANUAL',
  TIMEOUT_MANUAL: 'TIMEOUT_MANUAL',
  INSTITUTIONAL_MANUAL: 'INSTITUTIONAL_MANUAL'
};

export const VALIDATION_TIMEOUT_ERROR_CODE = 'VALIDATION_TIMEOUT';

/** Client axios wait: backend 12s hard deadline + 6s for the timeout payload to arrive. */
export const ID_VALIDATE_CLIENT_TIMEOUT_MS = 18000;

export const VALIDATION_TIMEOUT_USER_MESSAGE =
  'Validation timed out. You can enter your details manually to continue.';

export function isIpageErrorMessage(message) {
  return typeof message === 'string' && /iPage/i.test(message);
}

/**
 * Strict timeout detection. Connection refused / DNS / reset are not timeouts.
 */
export function isValidationTimeout({
  errorMessage,
  errorCode,
  isTimeout,
  axiosCode,
  code
} = {}) {
  if (isTimeout === true) return true;
  if (errorCode === VALIDATION_TIMEOUT_ERROR_CODE) return true;
  if (
    code === VALIDATION_TIMEOUT_ERROR_CODE ||
    code === 'ETIMEDOUT' ||
    code === 'ESOCKETTIMEDOUT'
  ) {
    return true;
  }
  if (axiosCode === 'ECONNABORTED') return true;

  const msg = String(errorMessage || '');
  if (!msg) return false;
  if (/\b(ECONNREFUSED|ENOTFOUND|ECONNRESET|ENETUNREACH)\b/i.test(msg)) return false;
  return /\btimeouts?\b/i.test(msg) || /timed\s*out/i.test(msg);
}

export function formatKraErrorMessage(message, extra = {}) {
  if (isValidationTimeout({ errorMessage: message, ...extra })) {
    return VALIDATION_TIMEOUT_USER_MESSAGE;
  }
  if (!message || typeof message !== 'string') return message;
  if (!isIpageErrorMessage(message)) return message;
  const trimmed = message.trim();
  return `${trimmed}${trimmed.endsWith('.') ? ' ' : '. '}Contact KRA for assistance.`;
}

/**
 * Resolve validationMethod after a failed lookup.
 * Timeout unlocks manual entry only when the failure is an actual timeout.
 * @param {{ isInstitutional: boolean, errorMessage?: string, requiresManualInput?: boolean, isTimeout?: boolean, errorCode?: string, axiosCode?: string, code?: string }} opts
 * @returns {''|'MANUAL'|'IPAGE_MANUAL'|'TIMEOUT_MANUAL'|'INSTITUTIONAL_MANUAL'}
 */
export function resolveFailedValidationMethod({
  isInstitutional,
  errorMessage,
  requiresManualInput = false,
  isTimeout = false,
  errorCode,
  axiosCode,
  code
} = {}) {
  if (isInstitutional) {
    return VALIDATION_METHOD.INSTITUTIONAL_MANUAL;
  }
  if (isIpageErrorMessage(errorMessage)) {
    return VALIDATION_METHOD.IPAGE_MANUAL;
  }
  if (
    isValidationTimeout({
      errorMessage,
      errorCode,
      isTimeout,
      axiosCode,
      code
    })
  ) {
    return VALIDATION_METHOD.TIMEOUT_MANUAL;
  }
  if (requiresManualInput) {
    return VALIDATION_METHOD.MANUAL;
  }
  return '';
}

export function allowsManualNameAndPin(validationMethod) {
  return (
    validationMethod === VALIDATION_METHOD.IPAGE_MANUAL ||
    validationMethod === VALIDATION_METHOD.TIMEOUT_MANUAL ||
    validationMethod === VALIDATION_METHOD.INSTITUTIONAL_MANUAL
  );
}

export function isNameFieldDisabled(validationMethod) {
  return validationMethod === VALIDATION_METHOD.MANUAL;
}

export function isKraPinFieldDisabled(validationMethod) {
  return (
    validationMethod === VALIDATION_METHOD.MANUAL ||
    validationMethod === VALIDATION_METHOD.KRA
  );
}

/**
 * Whether Continue can be enabled for the current validation state.
 */
export function isInsuredFormComplete({
  validationMethod,
  hasAllValues,
  phoneOk,
  kraSuccess,
  insuredName,
  kraPin
}) {
  const hasNameAndPin =
    Boolean(insuredName && String(insuredName).trim()) &&
    Boolean(kraPin && String(kraPin).trim());

  if (validationMethod === VALIDATION_METHOD.KRA) {
    return Boolean(hasAllValues && phoneOk && kraSuccess && hasNameAndPin);
  }
  if (validationMethod === VALIDATION_METHOD.IPRS) {
    return Boolean(hasAllValues && phoneOk && hasNameAndPin);
  }
  if (allowsManualNameAndPin(validationMethod)) {
    return Boolean(hasAllValues && phoneOk && hasNameAndPin);
  }
  if (validationMethod === VALIDATION_METHOD.MANUAL) {
    return false;
  }
  return Boolean(hasAllValues && phoneOk && kraSuccess && hasNameAndPin);
}
